import { FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import cn from 'classnames';
import dibsCss from 'dibs-css';
import { useIntl } from 'dibs-react-intl';
import CreditCardForm from 'dibs-credit-card-form';
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { type PaymentMethod, isPaymentProcessor } from '../../types';
import { PaymentMethodSelectOption } from '../PaymentMethodSelectOption';
import { messages } from './messages';
import {
    usePaymentMethodSelectContext,
    type CreditCardForm as CreditCardFormType,
} from '../PaymentMethodSelectContext';

type CreditCardField = keyof CreditCardFormType;

import { WalletCreditCardOption_creditCard$key } from './__generated__/WalletCreditCardOption_creditCard.graphql';
import { WalletCreditCardOption_viewer$key } from './__generated__/WalletCreditCardOption_viewer.graphql';

const creditCardFragment = graphql`
    fragment WalletCreditCardOption_creditCard on CreditCard {
        token
        cardLogo
        cardType
        cardExpiryMonth
        cardExpiryYear
        paymentProcessor
        formattedTextMaskedCardNumber
        cardHolderName
        maskedCardNumber
        hashedCardNumber
        billingAddress {
            firstName
            lastName
            line1
            line2
            country
            zipCode
            company
            state
            city
        }
    }
`;

const viewerFragment = graphql`
    fragment WalletCreditCardOption_viewer on Viewer {
        isCvcVerificationOn: featureFlag(feature: "checkoutCvcVerification")
    }
`;

type Props = {
    name: string;
    isChecked: boolean;
    dataTn: string;
    creditCard: WalletCreditCardOption_creditCard$key;
    viewer: WalletCreditCardOption_viewer$key;
    isPermissible: boolean;
    onChange: (paymentMethod: PaymentMethod) => void;
    onDelete: (paymentMethod: PaymentMethod) => void;
};

export const WalletCreditCardOption: FC<Props> = props => {
    const {
        isChecked,
        dataTn,
        name,
        onChange,
        onDelete,
        creditCard: creditCardRef,
        isPermissible,
        viewer: viewerRef,
    } = props;

    const intl = useIntl();
    const creditCard = useFragment(creditCardFragment, creditCardRef) || {};
    const viewer = useFragment(viewerFragment, viewerRef) || {};

    const {
        formattedTextMaskedCardNumber,
        cardHolderName,
        cardLogo,
        cardType,
        cardExpiryMonth,
        cardExpiryYear,
        maskedCardNumber,
        billingAddress,
    } = creditCard;

    const { firstName, lastName, line1, line2, country, zipCode, company, state, city } =
        billingAddress || {};

    const { creditCardForm, creditCardFormErrors, isBusy, updateCreditCardForm } =
        usePaymentMethodSelectContext();

    const { token, paymentProcessor, hashedCardNumber } = creditCard;

    if (!isPermissible && paymentProcessor === 'STRIPE' && !viewer?.isCvcVerificationOn) {
        return null;
    }

    function handleChange(): void {
        if (token && isPaymentProcessor(paymentProcessor)) {
            const shouldVerify = paymentProcessor === 'BRAINTREE' && !isPermissible;

            onChange({
                paymentMethodType: 'CREDIT_CARD',
                paymentMethodToken: token,
                paymentProcessor,
                billingAddress: shouldVerify
                    ? { firstName, lastName, line1, line2, country, zipCode, company, state, city }
                    : null,
            });

            if (shouldVerify) {
                updateCreditCardForm({
                    isVerifying: true,
                    cardHolderName: cardHolderName || '',
                    cardExpiry: `${cardExpiryMonth}/${cardExpiryYear?.slice(-2)}`,
                    creditCardNumber: '',
                    securityCode: '',
                });
            }
        }
    }

    function handleDeleteClick(): void {
        if (token && isPaymentProcessor(paymentProcessor)) {
            onDelete({
                paymentMethodType: 'CREDIT_CARD',
                paymentMethodToken: token,
                paymentProcessor,
                hashedCardNumber: hashedCardNumber || '',
            });
        }
    }

    function handleVerifyFormChange(payload: { field: CreditCardField; value: string }): void {
        const { field, value } = payload;
        updateCreditCardForm({ [field]: value });
    }

    return (
        <PaymentMethodSelectOption
            isChecked={isChecked}
            dataTn={dataTn}
            name={name}
            onChange={handleChange}
            value={token || ''}
            label={
                <>
                    {cardLogo && (
                        <img
                            className={cn(dibsCss.h16Px, dibsCss.mrSmall)}
                            src={cardLogo}
                            alt={cardType || ''}
                        />
                    )}
                    <div className={cn(dibsCss.flexGrow, dibsCss.flex, dibsCss.flexCol)}>
                        <span className={dibsCss.sassyFontBodyMediumHeavy}>
                            {formattedTextMaskedCardNumber}
                        </span>
                        <span className={dibsCss.sassyFontBodySmall}>
                            {cardExpiryMonth}/{cardExpiryYear}
                        </span>
                    </div>
                    <div>
                        <Link onClick={handleDeleteClick}>
                            {intl.formatMessage(messages.deleteButton)}
                        </Link>
                    </div>
                </>
            }
            content={
                isChecked && paymentProcessor === 'BRAINTREE' && !isPermissible ? (
                    <>
                        <div className={cn(dibsCss.mtSmall, dibsCss.mbSmall)}>
                            <p>
                                <span className={dibsCss.uppercase}>
                                    <FormattedMessage
                                        id="dibs-payment.WalletCreditCardOption.verifyCardHeader"
                                        defaultMessage="Please verify your card"
                                    />
                                </span>
                                <span className={cn(dibsCss.plXsmall, dibsCss.prXsmall)}>-</span>
                                <span>
                                    <FormattedMessage
                                        id="dibs-payment.WalletCreditCardOption.verifyCardDigits"
                                        defaultMessage="ending in {lastDigits}"
                                        values={{ lastDigits: maskedCardNumber?.slice(-4) }}
                                    />
                                </span>
                            </p>

                            <p className={dibsCss.mtSmall}>
                                <FormattedMessage
                                    id="checkout.CheckoutCreditCardForm.verifyCardText"
                                    defaultMessage="For security purposes, we require you to re-enter your credit card number and security code for each new shipping address."
                                />
                            </p>
                        </div>

                        <CreditCardForm
                            creditCard={creditCardForm}
                            isVerifying
                            validations={{ maskedCardNumber }}
                            disabled={['cardHolderName', 'cardExpiry']}
                            hideSubmit
                            disableForm={isBusy}
                            showAllErrors={!!Object.keys(creditCardFormErrors).length}
                            onUpdate={handleVerifyFormChange}
                            fullWidth
                        />
                    </>
                ) : null
            }
        />
    );
};
